import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Minus, Check, UserPlus } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AmmoSaleModal = ({ sessionId, memberId, memberName, onClose, onSuccess, isTrial = false, trialName = '', defaultTag = null, allowMemberSelection = false }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ammoTypes, setAmmoTypes] = useState([]);
  const [selectedAmmo, setSelectedAmmo] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [noInventoryMode, setNoInventoryMode] = useState(false);
  // Members list for manual member selection (when allowMemberSelection)
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(memberId || '');
  const [selectedMemberName, setSelectedMemberName] = useState(memberName || '');
  // Set default tag based on whether it's a trial session
  const [selectedTags, setSelectedTags] = useState(
    defaultTag ? [defaultTag] : (isTrial ? ['GJESTE-SKYTING'] : ['MEDLEM'])
  );
  
  // External buyer (non-member) state - auto-enable for trial sessions
  const [isExternalBuyer, setIsExternalBuyer] = useState(isTrial);
  const [externalBuyerName, setExternalBuyerName] = useState(trialName || '');

  useEffect(() => {
    fetchAmmoSettings();
    if (allowMemberSelection) {
      fetchMembers();
    }
  }, [allowMemberSelection]);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const active = (response.data || []).filter(u =>
        (u.role === 'member' || u.role === 'admin' || u.role === 'skyteleder' || u.role === 'org_admin' || u.role === 'range_officer')
        && u.approved && !u.is_paused && u.active !== false
      );
      // Sort by name
      active.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'no'));
      setAllMembers(active);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchAmmoSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Only show enabled ammo types
      const enabled = response.data.ammo_types.filter(a => a.enabled);
      setAmmoTypes(enabled);
      setNoInventoryMode(!!response.data.no_inventory_mode);
      
      // Set available tags
      setAvailableTags(response.data.ammo_tags || ['MEDLEM', 'SIKKERH.KURS', 'GJESTE-SKYTING', 'INNSKYTING']);
      
      // Initialize selectedAmmo with 0 for all types
      const initial = {};
      enabled.forEach(ammo => {
        initial[ammo.caliber] = 0;
      });
      setSelectedAmmo(initial);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ammo settings:', error);
      toast.error('Kunne ikke laste ammunisjonstyper');
      setLoading(false);
    }
  };

  const handleIncrement = (caliber) => {
    const ammo = ammoTypes.find(a => a.caliber === caliber);
    if (!ammo) return;
    
    const current = selectedAmmo[caliber] || 0;
    if (!noInventoryMode && current >= ammo.current_stock) {
      toast.error(`Kun ${ammo.current_stock} esker tilgjengelig for ${caliber}`);
      return;
    }
    
    setSelectedAmmo({
      ...selectedAmmo,
      [caliber]: current + 1
    });
  };

  const handleDecrement = (caliber) => {
    const current = selectedAmmo[caliber] || 0;
    if (current > 0) {
      setSelectedAmmo({
        ...selectedAmmo,
        [caliber]: current - 1
      });
    }
  };

  const handleManualInput = (caliber, value) => {
    const ammo = ammoTypes.find(a => a.caliber === caliber);
    if (!ammo) return;
    // Remove leading zeros, allow empty string while editing
    const cleaned = (value || '').replace(/^0+(?=\d)/, '');
    if (cleaned === '') {
      setSelectedAmmo({ ...selectedAmmo, [caliber]: 0 });
      return;
    }
    let num = parseInt(cleaned, 10);
    if (isNaN(num) || num < 0) num = 0;
    if (num > ammo.current_stock) {
      toast.error(`Kun ${ammo.current_stock} esker tilgjengelig for ${caliber}`);
      num = ammo.current_stock;
    }
    setSelectedAmmo({ ...selectedAmmo, [caliber]: num });
  };

  const handleRegister = async () => {
    // Prepare sales data (only non-zero amounts)
    const sales = Object.entries(selectedAmmo)
      .filter(([_, boxes]) => boxes > 0)
      .map(([caliber, boxes]) => ({ caliber, boxes_sold: boxes }));
    
    if (sales.length === 0) {
      toast.error('Velg minst én ammunisjonstype');
      return;
    }
    
    if (selectedTags.length === 0) {
      toast.error('Velg minst én tag');
      return;
    }
    
    // Validate member selection if dropdown mode
    if (allowMemberSelection && !isExternalBuyer && !selectedMemberId) {
      toast.error('Vennligst velg et medlem for salget');
      return;
    }

    // Validate external buyer name if mode is active
    if (isExternalBuyer && !externalBuyerName.trim()) {
      toast.error('Vennligst skriv inn navn på kjøper');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const effectiveMemberId = allowMemberSelection ? selectedMemberId : memberId;
      const effectiveMemberName = allowMemberSelection ? selectedMemberName : memberName;
      
      const payload = {
        member_id: isExternalBuyer ? null : effectiveMemberId,
        member_name: isExternalBuyer ? externalBuyerName.trim() : effectiveMemberName,
        sales,
        tags: selectedTags,
        is_external: isExternalBuyer  // Flag for external buyer
      };
      
      // Only include session_id if it's provided (for session-linked sales)
      if (sessionId && !isExternalBuyer) {
        payload.session_id = sessionId;
      }
      
      await axios.post(
        `${API_BASE}/api/admin/ammo-sale`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isExternalBuyer 
        ? `Eksternt salg registrert for ${externalBuyerName}!` 
        : 'Ammo-salg registrert!'
      );
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error registering ammo sale:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke registrere salg';
      toast.error(errorMsg);
      setSaving(false);
    }
  };

  const getStockColor = (ammo) => {
    const percentage = (ammo.current_stock / Math.max(ammo.current_stock, 10)) * 100;
    if (percentage <= 10) return 'text-red-400';
    if (percentage <= 30) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getTotalAmount = () => {
    let total = 0;
    Object.entries(selectedAmmo).forEach(([caliber, boxes]) => {
      const ammo = ammoTypes.find(a => a.caliber === caliber);
      if (ammo && boxes > 0) {
        total += boxes * ammo.price_per_box;
      }
    });
    return total;
  };

  const getTotalBoxes = () => {
    return Object.values(selectedAmmo).reduce((sum, boxes) => sum + boxes, 0);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-white">Laster...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <img src="/patron-symbol.svg" alt="Ammo" className="h-6 w-6" />
              Registrer ammo-salg
            </h3>
            
            {/* External buyer toggle and name input */}
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isExternalBuyer}
                  onChange={(e) => {
                    setIsExternalBuyer(e.target.checked);
                    if (!e.target.checked) {
                      setExternalBuyerName('');
                    }
                  }}
                  className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-orange-400 font-medium flex items-center gap-1">
                  <UserPlus className="h-4 w-4" />
                  Eksternt salg (ikke medlem)
                </span>
              </label>
              
              {isExternalBuyer ? (
                <input
                  type="text"
                  value={externalBuyerName}
                  onChange={(e) => setExternalBuyerName(e.target.value)}
                  placeholder="Skriv inn navn på kjøper..."
                  className="w-full px-3 py-2 bg-orange-900/20 border-2 border-orange-500/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
              ) : allowMemberSelection ? (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Velg medlem:</label>
                  <select
                    value={selectedMemberId}
                    onChange={(e) => {
                      const id = e.target.value;
                      const m = allMembers.find(u => u.id === id);
                      setSelectedMemberId(id);
                      setSelectedMemberName(m ? m.full_name : '');
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                    data-testid="ammo-member-select"
                  >
                    <option value="">— Velg medlem —</option>
                    {allMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.full_name} ({m.email})</option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Medlem: {memberName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded transition-colors self-start"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-3">
          {ammoTypes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">Ingen ammunisjonstyper tilgjengelig for salg.</p>
              <p className="text-gray-500 text-sm mt-2">Aktiver ammunisjonstyper i Innstillinger.</p>
            </div>
          ) : (
            ammoTypes.map((ammo) => (
              <div
                key={ammo.caliber}
                className="p-4 bg-gray-700 rounded-lg flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-medium">{ammo.caliber}</h4>
                    {!noInventoryMode && (
                      <span className={`text-sm font-medium ${getStockColor(ammo)}`}>
                        {ammo.current_stock} esker på lager
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                    <span>{ammo.rounds_per_box} skudd/eske</span>
                    <span>•</span>
                    <span>{ammo.price_per_box.toFixed(2)} kr/eske</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 ml-4">
                  <button
                    onClick={() => handleDecrement(ammo.caliber)}
                    disabled={!selectedAmmo[ammo.caliber] || selectedAmmo[ammo.caliber] === 0}
                    className="w-10 h-10 flex items-center justify-center bg-gray-600 hover:bg-gray-500 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>

                  <div className="w-16 text-center">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max={noInventoryMode ? undefined : ammo.current_stock}
                      value={selectedAmmo[ammo.caliber] || 0}
                      onChange={(e) => handleManualInput(ammo.caliber, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="w-full bg-transparent text-2xl font-bold text-yellow-400 text-center outline-none focus:ring-2 focus:ring-yellow-500 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      data-testid={`ammo-input-${ammo.caliber}`}
                      aria-label={`Antall esker ${ammo.caliber}`}
                    />
                  </div>

                  <button
                    onClick={() => handleIncrement(ammo.caliber)}
                    disabled={ammo.current_stock === 0 || (selectedAmmo[ammo.caliber] || 0) >= ammo.current_stock}
                    className="w-10 h-10 flex items-center justify-center bg-green-600 hover:bg-green-700 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Tags Section */}
        <div className="p-6 pt-0 border-t border-gray-700 mt-4">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Kategori
          </label>
          <div className="grid grid-cols-2 gap-2">
            {availableTags.map((tag) => (
              <label
                key={tag}
                className="flex items-center space-x-2 cursor-pointer p-3 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
              >
                <input
                  type="radio"
                  name="ammoSaleCategory"
                  checked={selectedTags.includes(tag)}
                  onChange={() => {
                    setSelectedTags([tag]);
                  }}
                  className="text-yellow-500 focus:ring-yellow-500"
                />
                <span className="text-sm text-white font-medium">{tag}</span>
              </label>
            ))}
          </div>
          {selectedTags.length > 0 && (
            <p className="text-xs text-gray-400 mt-2">
              Valgt kategori: {selectedTags[0]}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-400 text-sm">Totalt valgt:</p>
              <p className="text-white font-bold text-lg">{getTotalBoxes()} esker</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-sm">Sum:</p>
              <p className="text-yellow-400 font-bold text-2xl">{getTotalAmount().toFixed(2)} kr</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded font-medium transition-colors"
            >
              Avbryt
            </button>
            <button
              onClick={handleRegister}
              disabled={saving || getTotalBoxes() === 0}
              className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Check className="h-5 w-5" />
              {saving ? 'Registrerer...' : 'Registrer salg'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmmoSaleModal;
